import { useMemo } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Loader2, PhoneCall, PhoneOff, Users, Video } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { api, type MeetCall } from "@/services/api"
import { cn } from "@/lib/utils"

function callAge(createdAt: string) {
  const diff = Date.now() - new Date(createdAt).getTime()
  const minutes = Math.max(0, Math.floor(diff / 60000))
  if (minutes < 1) return "gerade eben"
  return `seit ${minutes} Min.`
}

function participantLabel(call: MeetCall) {
  if (call.participants.length === 0) return "Noch niemand im Gespräch"
  return call.participants
    .map((participant) => `${participant.name}${participant.role === "primary" ? " (nimmt an)" : ""}`)
    .join(", ")
}

function organizationLine(call: MeetCall) {
  return [call.organizationName, call.callerEmail].filter(Boolean).join(" · ")
}

export function MeetCallsButton() {
  const queryClient = useQueryClient()
  const { data: activeCalls = [] } = useQuery({
    queryKey: ["meet-calls", "active"],
    queryFn: () => api.meetCalls.active(),
    refetchInterval: 5000,
    staleTime: 2000,
  })

  const ringingCount = activeCalls.filter((call) => call.status === "ringing").length
  const hasRinging = ringingCount > 0

  const joinMutation = useMutation({
    mutationFn: async ({ call }: { call: MeetCall; popup: Window | null }) => {
      return api.meetCalls.join(call.id)
    },
    onSuccess: (joined, variables) => {
      queryClient.invalidateQueries({ queryKey: ["meet-calls"] })
      if (variables.popup) {
        variables.popup.location.href = joined.meetUrl
      } else {
        window.open(joined.meetUrl, "_blank", "noopener,noreferrer")
      }
    },
    onError: (_error, variables) => {
      variables.popup?.close()
      queryClient.invalidateQueries({ queryKey: ["meet-calls"] })
    },
  })

  const finishMutation = useMutation({
    mutationFn: (callId: string) => api.meetCalls.finish(callId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meet-calls"] })
      queryClient.invalidateQueries({ queryKey: ["notifications"] })
    },
  })

  const tooltipContent = useMemo(() => {
    if (activeCalls.length === 0) return null
    return activeCalls.map((call) => (
      <div key={call.id} className="space-y-0.5">
        <div className="font-medium">{call.callerName}</div>
        <div className="text-xs text-muted-foreground">{participantLabel(call)}</div>
      </div>
    ))
  }, [activeCalls])

  if (activeCalls.length === 0) return null

  function handleJoin(call: MeetCall) {
    const popup = window.open(
      "about:blank",
      `netzfabriks-erp-meet-${call.id}`,
      "popup=yes,width=1280,height=900,left=80,top=80",
    )
    if (popup) {
      popup.opener = null
    }
    joinMutation.mutate({ call, popup })
  }

  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant={hasRinging ? "default" : "outline"}
              size="icon"
              className={cn(
                "relative h-10 w-10 rounded-full",
                hasRinging && "animate-pulse shadow-md",
              )}
              title="Aktive Kunden-Meets"
            >
              {hasRinging ? <PhoneCall className="h-5 w-5" /> : <Video className="h-5 w-5" />}
              <Badge
                variant="destructive"
                className="absolute -right-1 -top-1 h-5 min-w-5 justify-center rounded-full px-1 text-[10px]"
              >
                {activeCalls.length}
              </Badge>
              <span className="sr-only">Aktive Kunden-Meets</span>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-80 space-y-2 text-left">
          {tooltipContent}
        </TooltipContent>
      </Tooltip>

      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <h4 className="text-sm font-semibold">Aktive Kunden-Meets</h4>
            <p className="text-xs text-muted-foreground">
              {ringingCount > 0 ? `${ringingCount} Anfrage${ringingCount === 1 ? "" : "n"} klingeln` : "Gespräche laufen"}
            </p>
          </div>
          <Users className="h-4 w-4 text-muted-foreground" />
        </div>
        <Separator />
        <div className="max-h-96 overflow-auto">
          {activeCalls.map((call, index) => (
            <div key={call.id}>
              {index > 0 && <Separator />}
              <div className="space-y-3 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">{call.callerName}</span>
                      <Badge variant={call.status === "ringing" ? "destructive" : "secondary"}>
                        {call.status === "ringing" ? "klingelt" : "angenommen"}
                      </Badge>
                    </div>
                    {organizationLine(call) && (
                      <p className="mt-0.5 truncate text-xs text-muted-foreground">
                        {organizationLine(call)}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">{callAge(call.createdAt)}</p>
                  </div>
                </div>

                <div className="rounded-md bg-muted/50 px-3 py-2 text-xs">
                  <div className="mb-1 font-medium">Teilnehmer</div>
                  {call.participants.length > 0 ? (
                    <div className="space-y-1">
                      {call.participants.map((participant) => (
                        <div key={participant.id} className="flex items-center justify-between gap-2">
                          <span className="truncate">{participant.name}</span>
                          {participant.role === "primary" && <Badge variant="outline">nimmt an</Badge>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground">Noch niemand hat angenommen.</div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => handleJoin(call)}
                    disabled={joinMutation.isPending}
                  >
                    {joinMutation.isPending ? (
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    ) : (
                      <Check className="mr-2 h-3 w-3" />
                    )}
                    {call.status === "ringing" ? "Annehmen" : "Beitreten"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => finishMutation.mutate(call.id)}
                    disabled={finishMutation.isPending}
                  >
                    <PhoneOff className="mr-2 h-3 w-3" />
                    Erledigt
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
